import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { Loader, CTASection } from "../components/ui-bits";
import { MediaImage } from "../components/MediaImage";

export default function VerticalDetail() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setV(null);
    api.get(`/verticals/${slug}`).then((r) => {
      setV(r.data);
      api.get(`/verticals?category=${encodeURIComponent(r.data.category)}`).then((rr) =>
        setRelated(rr.data.filter((x) => x.slug !== slug).slice(0, 3))
      );
    }).catch(() => setV(false));
  }, [slug]);

  if (v === null) return <Loader label="Loading" />;
  if (v === false) return <div className="max-w-3xl mx-auto px-6 py-40 text-center text-slate-400" data-testid="vertical-notfound">Vertical not found. <Link to="/verticals" className="text-emerald-400">Back to verticals</Link></div>;

  return (
    <div data-testid="vertical-detail-page">
      <section className="relative border-b border-slate-800">
        <div className="absolute inset-0">
          <MediaImage src={v.image_url} alt={v.name} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#030712]/70 to-[#030712]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <Link to="/verticals" data-testid="vertical-back-link" className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-400 text-sm font-mono mb-6">
            <ArrowLeft className="w-4 h-4" /> All Verticals
          </Link>
          <p className="eyebrow mb-3">{v.category}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold text-white tracking-tight leading-tight">{v.name}</h1>
          {v.excerpt && <p className="mt-6 text-lg text-slate-300 max-w-3xl">{v.excerpt}</p>}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-2xl overflow-hidden mb-12 border border-slate-800">
          <MediaImage src={v.image_url} alt={v.name} className="w-full max-h-[70vh] object-cover" />
        </div>
        <div className="prose-emerald max-w-none">
          {(v.body && v.body.length ? v.body : [v.excerpt]).filter(Boolean).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {v.gallery && v.gallery.length > 1 && (
          <div className="mt-14">
            <p className="eyebrow mb-6">Gallery</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {v.gallery.slice(1).map((g, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-slate-800">
                  <MediaImage src={g} alt={`${v.name} ${i + 1}`} className="w-full h-40 object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <p className="eyebrow mb-6">Related in {v.category}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.id} to={`/verticals/${r.slug}`} data-testid={`related-vertical-${r.slug}`} className="card-vertical rounded-2xl overflow-hidden group">
                <div className="h-36 overflow-hidden">
                  <MediaImage src={r.image_url} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-semibold text-white group-hover:text-emerald-400 transition-colors">{r.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <CTASection />
    </div>
  );
}
